import { EXCLUDED_OPSIUM_POLICIES, OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { PermissionRow, toUserGroupFieldKey, UserGroupPermissions } from '../types/permission';
import { UpdateUserGroupPoliciesRequest } from '../types/request/permission-request';
import { UserGroupPoliciesResponse } from '../types/response/permission-response';
import { isSuperAdminUserGroup } from '../utils/user-group.utils';

/**
 * Maps API response to domain model
 */
export const mapUserGroupPermissionsFromResponse = (
  response: UserGroupPoliciesResponse
): UserGroupPermissions => {
  return {
    id: response.id,
    name: response.name,
    policies: response.policies,
    isDefault: response.isDefault,
  };
};

/**
 * Maps array of API responses to domain model
 */
export const mapUserGroupPermissionsListFromResponse = (
  responses: UserGroupPoliciesResponse[]
): UserGroupPermissions[] => {
  return responses.map(mapUserGroupPermissionsFromResponse);
};

/**
 * Transforms user group permissions into permission rows for the table.
 * Each row represents one policy with its granted state per user group column.
 *
 * Note: FullAccess policy is excluded from the table rows.
 * If a user group has FullAccess, all their permissions are shown as enabled.
 */
export const transformToPermissionRows = (
  userGroupPermissions: UserGroupPermissions[],
  additionalExcludedPolicies: OpsiumPolicy[] = []
): PermissionRow[] => {
  const excludedPolicies = new Set([...EXCLUDED_OPSIUM_POLICIES, ...additionalExcludedPolicies]);

  // Get all policies except excluded ones (FullAccess, feature-flagged, etc.)
  const allPolicies = Object.values(OpsiumPolicy).filter(policy => !excludedPolicies.has(policy));

  // Column order follows the API order, so a rename never reshuffles the table
  const grants = userGroupPermissions.map(group => ({
    key: toUserGroupFieldKey(group.id),
    hasFullAccess: group.policies.includes(OpsiumPolicy.FullAccess),
    granted: new Set(group.policies),
  }));

  return allPolicies.map(policy => ({
    policy,
    groups: Object.fromEntries(
      grants.map(grant => [grant.key, grant.hasFullAccess || grant.granted.has(policy)])
    ),
  }));
};

/**
 * Transforms permission rows back into user group permissions.
 * Used to prepare data for the API update request.
 *
 * Iteration is driven by the server list rather than the form keys, so a column for a group
 * that no longer exists can never leak into the payload.
 *
 * Note: If a user group originally had FullAccess, it is preserved — the table shows expanded
 * permissions but the API keeps FullAccess.
 *
 * Note: `hiddenPolicies` (e.g. policies excluded from display via feature flags) are not
 * present in `permissionRows` at all, so they are carried over unchanged from the original
 * data instead of being dropped.
 */
export const transformFromPermissionRows = (
  permissionRows: PermissionRow[],
  userGroupPermissions: UserGroupPermissions[],
  hiddenPolicies: OpsiumPolicy[] = []
): UserGroupPermissions[] => {
  const hiddenPolicySet = new Set(hiddenPolicies);

  // Group columns the table actually rendered. A group the server knows about but the form
  // never saw (e.g. created in another tab after this page loaded) keeps its policies
  // untouched instead of being wiped.
  const renderedGroupKeys = new Set(Object.keys(permissionRows[0]?.groups ?? {}));

  const fullAccessGroupIds = new Set<number>();
  const nextPolicies = new Map<number, OpsiumPolicy[]>();

  userGroupPermissions.forEach(group => {
    if (group.policies.includes(OpsiumPolicy.FullAccess)) {
      fullAccessGroupIds.add(group.id);
      nextPolicies.set(group.id, [OpsiumPolicy.FullAccess]);
      return;
    }

    if (!renderedGroupKeys.has(toUserGroupFieldKey(group.id))) {
      nextPolicies.set(group.id, group.policies);
      return;
    }

    // Seed with previously granted hidden policies so they aren't wiped out on save
    nextPolicies.set(
      group.id,
      group.policies.filter(policy => hiddenPolicySet.has(policy))
    );
  });

  // Build policy arrays from the table state (skip FullAccess and non-rendered groups)
  permissionRows.forEach(row => {
    userGroupPermissions.forEach(group => {
      const groupKey = toUserGroupFieldKey(group.id);
      if (fullAccessGroupIds.has(group.id) || !renderedGroupKeys.has(groupKey)) return;
      if (row.groups[groupKey]) nextPolicies.get(group.id)?.push(row.policy);
    });
  });

  return userGroupPermissions.map(group => ({
    ...group,
    policies: nextPolicies.get(group.id) ?? [],
  }));
};

/**
 * Maps user group permissions to API update request.
 *
 * Super Admin is the one group the backend rejects — and it requires every other group to be
 * present, so nothing else may be filtered out here.
 *
 * Only `id` and `policies` are sent. `isDefault` is deliberately not echoed back, so saving the
 * permissions matrix can never clobber the default group — that flag moves only through
 * PATCH /opsium-user-groups/:id/default or by deleting the current default.
 */
export const mapToUpdatePermissionsRequest = (
  userGroupPermissions: UserGroupPermissions[]
): UpdateUserGroupPoliciesRequest => {
  return {
    userGroups: userGroupPermissions
      .filter(group => !isSuperAdminUserGroup(group))
      .map(group => ({
        id: group.id,
        policies: group.policies,
      })),
  };
};
